import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Installation } from '@shared/schema';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';

export default function InstallationCalendar({ search }: { search?: string }) {
  const { toast } = useToast();
  const { data: installations = [] } = useQuery<Installation[]>({
    queryKey: ['/api/installations/grid-view', { q: search || '' }],
    queryFn: async () => {
      const qs = new URLSearchParams({ limit: '1000', ...(search ? { q: search } : {}) });
      const res = await fetch(`/api/installations/grid-view?${qs.toString()}`);
      if (!res.ok) throw new Error('Failed to load installations');
      return res.json();
    },
  });
  const datesMap = useMemo(() => {
    const map = new Map<string, Installation[]>();
    for (const i of installations) {
      const d = i.installationDate ? toKey(new Date(i.installationDate as any)) : null;
      if (!d) continue;
      if (!map.has(d)) map.set(d, []);
      map.get(d)!.push(i);
    }
    return map;
  }, [installations]);

  const [selected, setSelected] = useState<Date | undefined>(new Date());
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const selKey = selected ? toKey(selected) : '';
  const list = (selKey && datesMap.get(selKey)) || [];

  const modifiers = {
    hasInstall: Array.from(datesMap.keys()).map((k) => fromKey(k)),
  } as any;
  const modifiersStyles = {
    hasInstall: { backgroundColor: 'rgba(34,197,94,0.15)', borderRadius: 6 },
  } as any;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Calendar</CardTitle>
          <CardDescription>Installation dates highlighted</CardDescription>
        </CardHeader>
        <CardContent>
          <DayPicker
            mode="single"
            selected={selected}
            onSelect={setSelected}
            showOutsideDays
            modifiers={modifiers}
            modifiersStyles={modifiersStyles}
            weekStartsOn={1}
            className={cn('p-3')}
            classNames={{
              months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
              month: 'space-y-4',
              caption: 'flex justify-center pt-1 relative items-center',
              caption_label: 'text-sm font-medium',
              nav: 'space-x-1 flex items-center',
              nav_button: cn(
                buttonVariants({ variant: 'outline' }),
                'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
              ),
              nav_button_previous: 'absolute left-1',
              nav_button_next: 'absolute right-1',
              table: 'w-full border-collapse space-y-1',
              head_row: 'flex',
              head_cell: 'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]',
              row: 'flex w-full mt-2',
              cell: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
              day: cn(
                buttonVariants({ variant: 'ghost' }),
                'h-9 w-9 p-0 font-normal aria-selected:opacity-100'
              ),
              day_range_end: 'day-range-end',
              day_selected:
                'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
              day_today: 'bg-accent text-accent-foreground',
              day_outside:
                'day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
              day_disabled: 'text-muted-foreground opacity-50',
              day_range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
              day_hidden: 'invisible',
            }}
            components={{
              IconLeft: () => <ChevronLeft className="h-4 w-4" />,
              IconRight: () => <ChevronRight className="h-4 w-4" />,
              DayContent: (props: any) => {
                const date: Date = props.date;
                const key = toKey(date);
                const isDragOver = dragOverKey === key;
                return (
                  <span
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOverKey(key);
                    }}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      setDragOverKey(key);
                    }}
                    onDragLeave={() => setDragOverKey((cur) => (cur === key ? null : cur))}
                    onDrop={async (e) => {
                      e.preventDefault();
                      const raw = e.dataTransfer.getData('text/plain');
                      const installId = Number(raw);
                      if (!installId) return;
                      setDragOverKey(null);
                      try {
                        // Update backend with new installationDate (YYYY-MM-DD format acceptable)
                        await apiRequest({ method: 'PATCH', url: `/api/installations/${installId}`, data: { installationDate: key } });
                        queryClient.invalidateQueries({ queryKey: ['/api/installations'] });
                        setSelected(date);
                        toast({ title: 'Installation rescheduled', description: `#${installId} → ${date.toLocaleDateString()}` });
                      } catch (err: any) {
                        toast({ title: 'Reschedule failed', description: err?.message || 'Server error', variant: 'destructive' });
                      }
                    }}
                    style={isDragOver ? { boxShadow: 'inset 0 0 0 2px rgba(59,130,246,0.8)', borderRadius: 4 } : undefined}
                    className="inline-flex items-center justify-center w-9 h-9"
                  >
                    {date.getDate()}
                  </span>
                );
              },
            }}
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>
            {selected ? selected.toLocaleDateString() : 'No date selected'}
          </CardTitle>
          <CardDescription>{list.length} installation{list.length === 1 ? '' : 's'}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {list.map((i) => (
              <div
                key={i.id}
                className="rounded border p-2 text-sm flex items-center justify-between cursor-grab active:cursor-grabbing"
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', String(i.id));
                  // Optional: set a drag image to improve UX
                  // e.dataTransfer.setDragImage((e.target as HTMLElement), 10, 10);
                }}
              >
                <div>
                  <div className="font-medium">#{i.id} {(i as any).customer?.name || ''}</div>
                  <div className="text-muted-foreground">{i.installationAddress || (i as any).customer?.address || ''}</div>
                </div>
                <Badge variant="outline">{(i.status || '').replaceAll('_',' ')}</Badge>
              </div>
            ))}
            {list.length === 0 && <div className="text-sm text-muted-foreground">No installations for this date.</div>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function toKey(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
function fromKey(k: string) {
  const [y, m, d] = k.split('-').map((x) => Number(x));
  return new Date(y, m - 1, d);
}
